import API from '@/APIs/API';
import {InstantIulApplication} from '@/Core/agenium/types';
import {CalcfocusReportRequest} from '@amplify/athena-models';
import _ from 'lodash';

export async function initInstantIulApplication(
    customerId: string,
    {
        email,
        ageniumCaseId,
        dealId,
        bootstrapData,
    }: {email: string; ageniumCaseId?: string; dealId?: string; bootstrapData?: {key: string; value: string}[]},
): Promise<string> {
    let resp;
    if (_.isArray(bootstrapData)) {
        resp = await API.Call<{data: {applicationId: string}}>(
            `v2/internal/amplify-iul/create?customerId=${customerId}`,
            'POST',
            {
                ageniumCaseId,
                email,
                dealId,
                bootstrapData,
            },
        );
    } else {
        resp = await API.Call<{data: {applicationId: string}}>(
            `v2/internal/amplify-iul/create?customerId=${customerId}`,
            'POST',
            {
                ageniumCaseId,
                email,
                dealId,
            },
        );
    }
    if (!resp) {
        throw new Error('Error initializing application');
    }
    return resp.data.applicationId;
}

export async function approveInstantIulApplication(
    customerId: string,
    applicationId: string,
    policyNumber: string,
): Promise<void> {
    await API.Call<any>(
        `v2/internal/amplify-iul/approved?customerId=${customerId}&applicationId=${applicationId}`,
        'POST',
        {policyNumber},
    );
}

export async function declineInstantIulApplication(
    customerId: string,
    applicationId: string,
    {
        policyNumber,
        declineReason,
        calcfocusReportRequest,
    }: {policyNumber: string; declineReason: string; calcfocusReportRequest: CalcfocusReportRequest},
): Promise<void> {
    await API.Call<any>(
        `v2/internal/amplify-iul/declined?customerId=${customerId}&applicationId=${applicationId}`,
        'POST',
        {policyNumber, declineReason, calcfocusReportRequest},
    );
}

export async function cancelledInstantIulApplication(
    customerId: string,
    applicationId: string,
    {
        policyNumber,
        declineReason,
        calcfocusReportRequest,
    }: {policyNumber: string; declineReason: string; calcfocusReportRequest: CalcfocusReportRequest},
): Promise<void> {
    await API.Call<any>(
        `v2/internal/amplify-iul/cancelled?customerId=${customerId}&applicationId=${applicationId}`,
        'POST',
        {policyNumber, declineReason, calcfocusReportRequest},
    );
}

export async function referInstantIulApplication(
    customerId: string,
    applicationId: string,
    {policyNumber, calcfocusReportRequest}: {policyNumber: string; calcfocusReportRequest: CalcfocusReportRequest},
): Promise<void> {
    await API.Call<any>(
        `v2/internal/amplify-iul/referred?customerId=${customerId}&applicationId=${applicationId}`,
        'POST',
        {policyNumber, calcfocusReportRequest},
    );
}

export async function submitDocusign(
    customerId: string,
    applicationId: string,
    calcfocusReportRequest: CalcfocusReportRequest,
): Promise<void> {
    await API.Call<any>(
        `v2/internal/amplify-iul/submit?customerId=${customerId}&applicationId=${applicationId}`,
        'POST',
        {calcfocusReportRequest},
    );
}

export async function getApplicationRequirements(
    customerId: string,
    applicationId: string,
): Promise<{status: string; type: string}[]> {
    const resp = await API.Call<any>(
        `v2/internal/amplify-iul/application-requirements?customerId=${customerId}&applicationId=${applicationId}`,
        'GET',
    );
    return resp.data.requirements;
}

export async function getApplication(customerId: string): Promise<InstantIulApplication> {
    const resp = await API.Call<any>(`v2/internal/amplify-iul/application?customerId=${customerId}`, 'GET');
    return resp.data;
}

export async function getApplicationData(customerId: string): Promise<any> {
    const resp = await API.Call<any>(`v2/internal/amplify-iul/data?customerId=${customerId}`, 'GET');
    return resp?.data;
}

export async function updateApplicationData(customerId: string, data: any): Promise<any> {
    const resp = await API.Call<any>(`v2/internal/amplify-iul/data?customerId=${customerId}`, 'PUT', data);
    return resp?.data;
}

export async function sendConditionalCoverageEmail(customerId: string): Promise<void> {
    await API.Call<any>(`v2/internal/amplify-iul/conditional-coverage-email`, 'POST', {customerId});
}
